import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import {
    Button,
    Row,
    Col,
    Input,
    Form,
    Modal,
    Typography,
    Select,
    Tooltip,
    Divider,
    Switch,
    Flex,
    Upload,
    notification,
    UploadFile,
    UploadProps,
    Tag,
} from 'antd';
import { PaperClipOutlined, QuestionCircleOutlined } from '@ant-design/icons';

import IBaseComponentProps from '@components/base-component-props';
import Processing from '@controls/processing';

import { getDate } from '@extensions/utils';
import { exception } from '@extensions/notification';

import { userLoaded } from '@store/actions';
import { useAppDispatch } from '@store/hooks';

import { ITag } from '@entities/tag';
import { ITruck } from '@entities/truck';

import { TruckStatus } from '@enums/truck-status';

interface NewNotificationProps extends IBaseComponentProps {
    serviceName: string;
}

interface INotificationParams {
    subject?: string;
    body: string | undefined;
    recipients?: string;
    tags?: Array<string>;
    truckIds?: Array<string>;
    sendSms?: boolean;
    sendEmail?: boolean;
    sendPush?: boolean;
    sendTelegram?: boolean;
    sendMax?: boolean;
    fileIds?: Array<string>;
}

const NewNotification = (props: NewNotificationProps) => {
    const { serviceName, modulePath, serverFetch } = props;

    const { Text } = Typography;

    const Buffer = require('buffer/').Buffer;

    const navigate = useNavigate();

    const { TextArea } = Input;

    const [notificationForm] = Form.useForm();

    const sendPush = Form.useWatch('sendPush', notificationForm);
    const sendSms = Form.useWatch('sendSms', notificationForm);
    const sendTelegram = Form.useWatch('sendTelegram', notificationForm);
    const sendMax = Form.useWatch('sendMax', notificationForm);
    const sendEmail = Form.useWatch('sendEmail', notificationForm);
    const messageBody = Form.useWatch('body', notificationForm);

    const d = useAppDispatch();

    const [modal, modalContextHolder] = Modal.useModal();
    const [api, contextHolder] = notification.useNotification();

    const [files, setFiles] = useState<Array<UploadFile>>([]);
    const [recipients, setRecipients] = useState<string>();
    const [tags, setTags] = useState<Array<ITag>>([]);
    const [selectedTags, setSelectedTags] = useState<Array<string>>([]);

    const [trucks, setTrucks] = useState<Array<ITruck>>([]);
    const [selectedTrucks, setSelectedTrucks] = useState<Array<string>>([]);

    const [sending, setSending] = useState<boolean>(false);

    useEffect(() => {
        let cleanup = false;

        const fetchData = async () => {
            const promises = [
                await serverFetch('tags', { method: 'GET' })
                    .then((data: Array<ITag>) => {
                        return data;
                    })
                    .catch((ex: any) => {
                        exception(api, 'Ошибка получения тегов', ex, () => d(userLoaded(undefined)));
                    }),
                await serverFetch('trucks/import', { method: 'GET', queryParams: { statuses: [TruckStatus.OnWay] } })
                    .then((data: any) => {
                        return data;
                    })
                    .catch((ex: any) => {
                        exception(api, 'Ошибка получения машин', ex, () => d(userLoaded(undefined)));
                    }),
            ];

            Promise.all([promises]).then((result) => {
                if (cleanup) return;

                setTags(result[0][0] || []);
                setTrucks(result[0][1] || []);
            });
        };

        fetchData();

        return () => {
            cleanup = true;
        };
    }, []);

    const onSendNotification = (entity: INotificationParams) => {
        entity.recipients = recipients;
        entity.truckIds = selectedTrucks.map((t) => t);
        entity.tags = selectedTags.map((t) => t);
        entity.fileIds = files.map((f) => f.uid);

        let confirmInfo: any;
        if (selectedTrucks.length <= 0 && selectedTags.length <= 0 && (!recipients || recipients.length <= 0)) {
            confirmInfo = (
                <div>
                    Запустить массовую рассылку на сервисе{' '}
                    <span style={{ color: 'var(--main-red)', fontWeight: 600 }}>"{serviceName}"</span>?
                </div>
            );
        } else {
            confirmInfo = (
                <div>
                    Запустить рассылку на сервисе <span style={{ color: 'var(--main-red)', fontWeight: 600 }}>"{serviceName}"</span>?
                </div>
            );
        }

        modal.confirm({
            title: confirmInfo,
            icon: <QuestionCircleOutlined />,
            okType: 'primary',
            okText: 'ОК',
            cancelText: 'Отмена',
            onOk: () => onSend(entity),
        });
    };

    const onSend = (entity: INotificationParams) => {
        setSending(true);

        serverFetch(`notifications`, { method: 'POST', bodyData: entity })
            .then(() => {
                setSending(false);

                notificationForm.resetFields();

                if (entity.sendSms) {
                    navigate(`/${modulePath}/notifications/sms`);
                } else if (entity.sendTelegram) {
                    navigate(`/${modulePath}/notifications/telegram`);
                } else if (entity.sendMax) {
                    navigate(`/${modulePath}/notifications/max`);
                } else if (entity.sendEmail) {
                    navigate(`/${modulePath}/notifications/emails`);
                } else {
                    navigate(`/${modulePath}/notifications/push`);
                }
            })
            .catch((ex: any) => {
                setSending(false);
                exception(api, 'Ошибка отправки сообщений', ex, () => d(userLoaded(undefined)));
            });
    };

    const handlePreview = async (file: any) => {
        if (file) {
            if (!file.url && !file.preview) {
                let fileObj = file.originFileObj;

                if (typeof fileObj !== 'object') {
                    const buffer = new Buffer.from(file.originFileObj, 'base64');
                    fileObj = new Blob([buffer], { type: file.contentType });
                }

                const fileURL = URL.createObjectURL(fileObj);
                window.open(fileURL);
            }
        }
    };

    const handleChange: UploadProps['onChange'] = async ({ file, fileList, event }) => {
        if (!file) return;

        if (file.status == 'uploading' && event === undefined) {
            const result = await onUploadFile(file.originFileObj as Blob);

            if (result) {
                file.status = 'done';
                file.uid = result;
            } else {
                file.status = 'error';
            }
        } else if (file.status == 'removed') {
            const result = await onRemoveFile(file.uid);
            if (result) {
                file.status = 'done';
            } else {
                file.status = 'error';
            }
        }

        setFiles(fileList);
    };

    const onUploadFile = async (file: Blob): Promise<string> => {
        const formData = new FormData();
        formData.append('formFile', file);

        return serverFetch(`notifications/attachment/upload`, {
            method: 'POST',
            body: formData,
        })
            .then((data: any) => {
                return data;
            })
            .catch((ex: any) => {
                exception(api, 'Ошибка загрузки файла', ex, () => d(userLoaded(undefined)));
                return undefined;
            });
    };

    const onRemoveFile = async (id: string): Promise<string> => {
        return serverFetch(`notifications/attachment/${id}/delete`, { method: 'DELETE' })
            .then((data: any) => {
                return data;
            })
            .catch((ex: any) => {
                exception(api, 'Ошибка удаления файла', ex, () => d(userLoaded(undefined)));
                return undefined;
            });
    };

    return (
        <div className='scrollable-container'>
            <Row gutter={36}>
                <Col span={8}>
                    <Divider style={{ marginTop: 0 }} titlePlacement='left'>
                        Новое сообщение
                    </Divider>
                    <Form colon={false} wrapperCol={{ span: 24 }} onFinish={onSendNotification} form={notificationForm}>
                        <Form.Item
                            name='type'
                            rules={[
                                {
                                    required: !sendPush && !sendTelegram && !sendMax && !sendEmail && !sendSms,
                                    message: 'Укажите тип уведомления',
                                },
                            ]}
                        >
                            <Flex wrap>
                                <Form.Item style={{ margin: 0, marginRight: 25 }} label='Push' valuePropName='checked' name='sendPush'>
                                    <Switch size='small' />
                                </Form.Item>
                                <Form.Item
                                    style={{ margin: 0, marginRight: 25 }}
                                    label='Telegram'
                                    valuePropName='checked'
                                    name='sendTelegram'
                                >
                                    <Switch size='small' />
                                </Form.Item>
                                <Form.Item style={{ margin: 0, marginRight: 25 }} label='Max' valuePropName='checked' name='sendMax'>
                                    <Switch size='small' />
                                </Form.Item>
                                <Form.Item style={{ margin: 0, marginRight: 25 }} label='Email' valuePropName='checked' name='sendEmail'>
                                    <Switch size='small' />
                                </Form.Item>
                                <Form.Item style={{ margin: 0, marginRight: 25 }} label='Sms' valuePropName='checked' name='sendSms'>
                                    <Switch size='small' />
                                </Form.Item>
                            </Flex>
                        </Form.Item>

                        <Form.Item name='subject' rules={[{ required: sendEmail || sendPush, message: 'Укажите заголовок' }]}>
                            <Input placeholder='Заголовок...' maxLength={512} />
                        </Form.Item>

                        <Form.Item
                            name='body'
                            style={{ marginBottom: 30 }}
                            rules={[{ required: true, message: 'Введите текст сообщения' }]}
                        >
                            <TextArea placeholder='Cообщение...' rows={11} showCount />
                        </Form.Item>

                        <Divider style={{ marginBottom: 10 }} titlePlacement='left'>
                            Файлы
                        </Divider>
                        <Form.Item wrapperCol={{ span: 24 }}>
                            <Upload
                                onChange={(value) => {
                                    if (handleChange) handleChange(value);
                                }}
                                onPreview={handlePreview}
                                listType='text'
                                multiple
                                fileList={files}
                            >
                                <Tooltip title='Прикрепить файл'>
                                    <Button size='small' type='primary' icon={<PaperClipOutlined />}></Button>
                                </Tooltip>
                                {files && files?.length <= 0 && <span style={{ marginLeft: 10 }}>Прикрепить файл...</span>}
                            </Upload>
                        </Form.Item>

                        <Form.Item wrapperCol={{ span: 24 }}>
                            <Button type='primary' htmlType='submit' style={{ float: 'right' }}>
                                Отправить
                            </Button>
                        </Form.Item>
                    </Form>
                </Col>
                <Col span={16}>
                    {(sendPush || sendTelegram || sendMax || sendEmail) && (
                        <div style={{ marginBottom: 30 }}>
                            <Divider style={{ marginTop: 0 }} titlePlacement='left'>
                                Предварительный просмотр
                            </Divider>
                            <Row gutter={36}>
                                {(sendPush || sendTelegram || sendMax || sendEmail) && (
                                    <Col span={12}>
                                        <Flex gap='small' style={{ marginTop: 5, marginBottom: 20 }}>
                                            {sendPush && (
                                                <Tag variant='solid' color='green' styles={{ root: { fontSize: 16, fontWeight: 600 } }}>
                                                    Push
                                                </Tag>
                                            )}
                                            {sendTelegram && (
                                                <Tag variant='solid' color='blue' styles={{ root: { fontSize: 16, fontWeight: 600 } }}>
                                                    Telegram
                                                </Tag>
                                            )}
                                            {sendMax && (
                                                <Tag variant='solid' color='purple' styles={{ root: { fontSize: 16, fontWeight: 600 } }}>
                                                    Max
                                                </Tag>
                                            )}
                                            {sendEmail && (
                                                <Tag variant='solid' color='orange' styles={{ root: { fontSize: 16, fontWeight: 600 } }}>
                                                    Email
                                                </Tag>
                                            )}
                                        </Flex>
                                        <TextArea
                                            disabled
                                            rows={11}
                                            value={
                                                messageBody
                                                    ? `Уважаемый клиент, userLogin!\n${messageBody}\nС уважением, Транспортная Компания FreshLine.`
                                                    : ''
                                            }
                                        />
                                    </Col>
                                )}
                                {sendSms && (
                                    <Col span={12}>
                                        <Tag
                                            variant='solid'
                                            color='magenta'
                                            style={{ marginTop: 5, marginBottom: 20 }}
                                            styles={{ root: { fontSize: 16, fontWeight: 600 } }}
                                        >
                                            Sms
                                        </Tag>
                                        <TextArea
                                            disabled
                                            rows={10}
                                            value={
                                                messageBody
                                                    ? `Уважаемый клиент, userLogin!\n${messageBody}\nС уважением, Транспортная Компания FreshLine.`
                                                    : ''
                                            }
                                        />
                                        <Form.Item>
                                            <Text strong style={{ color: 'red' }}>
                                                Кол-во сегментов: {Math.ceil((messageBody?.length ?? 0) / 70)}
                                            </Text>
                                        </Form.Item>
                                    </Col>
                                )}
                            </Row>
                        </div>
                    )}

                    <Divider style={{ marginTop: 0 }} titlePlacement='left'>
                        Импорт
                    </Divider>
                    <Select
                        mode='multiple'
                        style={{ width: '100%' }}
                        allowClear={true}
                        onChange={(e) => {
                            if (!e) {
                                setSelectedTrucks([]);
                                return;
                            }

                            setSelectedTrucks(e);
                        }}
                        showSearch={{
                            optionFilterProp: 'children',
                            filterOption: (input, option) => (option?.label as string).toLowerCase().startsWith(input.toLowerCase()),
                        }}
                        options={trucks.map((t) => {
                            return { value: t.id, label: `${t.number} - ${getDate(t.departureOn)}` };
                        })}
                    />

                    <Divider style={{ marginTop: 20 }} titlePlacement='left'>
                        Получатели
                    </Divider>
                    <TextArea
                        placeholder='100,101,FL102,FL103...'
                        rows={2}
                        onInput={(e) => {
                            (e.target as HTMLInputElement).value = (e.target as HTMLInputElement).value
                                .replaceAll('\n', ',')
                                .replaceAll(' ', '');
                        }}
                        onChange={(e) => {
                            setRecipients(e.target.value);
                        }}
                    />

                    <Divider style={{ marginTop: 20 }} titlePlacement='left'>
                        Теги
                    </Divider>
                    <Select
                        mode='multiple'
                        style={{ width: '100%' }}
                        allowClear={true}
                        onChange={(e) => {
                            if (!e) {
                                setSelectedTags([]);
                                return;
                            }

                            setSelectedTags(e);
                        }}
                        showSearch={{
                            filterOption: (input, option) => (option?.label as string).toLowerCase().includes(input.toLowerCase()),
                            filterSort: (a, b) => (a?.label as string).toLowerCase().localeCompare((b?.label as string).toLowerCase()),
                        }}
                        options={tags.map((c) => {
                            return { value: c.name, label: `#${c.name}` };
                        })}
                    />
                </Col>
            </Row>

            {sending && <Processing title='Отправка сообщений...' />}
            {contextHolder}
            {modalContextHolder}
        </div>
    );
};

export default NewNotification;
